// context/UserContext.tsx
"use client"
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { usePathname } from "next/navigation";

// Solo las rutas que consumen el usuario necesitan verificar la sesión.
// La landing, las páginas legales y el login no, así evitamos un /auth/me 401
// innecesario (el login hace su propio setUser tras autenticar).
// OJO: son URLs, no rutas de fichero. Los route groups de app/ —(municipal),
// (ciudadano), (auth)— no aparecen en usePathname(), así que esta lista NO
// lleva paréntesis y no hay que tocarla al mover carpetas entre grupos.
const AUTH_PATHS = ["/dashboard", "/ciudadano", "/auth/callback"];
const needsAuth = (pathname: string) =>
  AUTH_PATHS.some((p) => pathname.startsWith(p));

export interface User {
  mail: string;
  tipo: number;
  idConcello: string;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loadingUser: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    // En rutas públicas (landing, legales) no hace falta verificar sesión.
    if (!needsAuth(pathname)) {
      setUser(null);
      setLoadingUser(false);
      return;
    }

    // Verificar si hay sesión activa al cargar
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_BACK_URI}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    };

    checkAuth();
  }, [pathname]);

  return (
    <UserContext.Provider value={{ user, setUser, loadingUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within UserProvider");
  return context;
};